import React from "react";
import { ChangeEvent, FormEvent } from "react";
import ReactModal from "react-modal";
import { Prompt, RouteComponentProps } from "react-router";

import { V2 } from "../../Api";
import withTitle from "../withTitle";

import OverflowMenuItem from "../OverflowMenuItem";
import ToolbarMenuItem from "../ToolbarMenuItem";
import ValidateInput from "../ValidateInput";
import ViewToolbar from "../ViewToolbar";

import "../../blocks.css";
import "../../modal.css";
import "../../schedule.css";
import "../viewer.css";
import Checkbox from "../Checkbox";

interface ISurgeryDeviceCreationViewState {
    changedFields: string[];
    hasChanged: boolean;
    isEdit: boolean;
    isLoading: boolean | string;
    isModalOpen: boolean;
    submitStatus: boolean | string;

    name: string;

    usesExpiration: boolean;
    usesLot: boolean;
    usesSerial: boolean;
    usesSize: boolean;

    [name: string]: any;
}

class SurgeryDeviceCreationView extends React.Component<RouteComponentProps<any>, ISurgeryDeviceCreationViewState> {
    private inputs: ValidateInput[];

    constructor(props: Readonly<RouteComponentProps<any>>) {
        super(props);

        this.state = {
            changedFields: [],
            hasChanged: false,
            isEdit: props.match.params.id !== undefined,
            isLoading: !!this.props.match.params.id,
            isModalOpen: false,
            submitStatus: false,

            name: "",

            usesExpiration: false,
            usesLot: false,
            usesSerial: false,
            usesSize: false,
        };

        this.inputs = [];

        this.receiveValidationRef = this.receiveValidationRef.bind(this);
        this.formInputChanged = this.formInputChanged.bind(this);
        this.submitForm = this.submitForm.bind(this);
        this.cancelForm = this.cancelForm.bind(this);
        this.handleDelete = this.handleDelete.bind(this);
        this.doRealDelete = this.doRealDelete.bind(this);
        this.closeDialog = this.closeDialog.bind(this);
    }

    public async componentDidMount() {
        if (!this.props.match.params.id) {
            return;
        }

        try {
            const device = await V2.get<any>(`glossary/devices/${this.props.match.params.id}`);

            this.setState({
                isLoading: false,

                name: device.name,

                usesExpiration: device.usesExpiration,
                usesLot: device.usesLot,
                usesSerial: device.usesSerial,
                usesSize: device.usesSize,
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message
            });
        }
    }

    public render() {
        if (typeof this.state.isLoading === "string") {
            return <div className="error-box">{this.state.isLoading}</div>;
        }

        if (this.state.isLoading) {
            return <div className="spinner spinner-center lds-dual-ring-big" />
        }

        return (
            <div className="viewer-root with-actionbar">
                <ViewToolbar>
                    <OverflowMenuItem>
                        <ToolbarMenuItem condition={this.state.isEdit}>
                            <button type="button" onClick={this.handleDelete}><i className="icon-trash" /><span>Delete Device</span></button>
                        </ToolbarMenuItem>
                    </OverflowMenuItem>
                </ViewToolbar>
                <Prompt when={this.state.hasChanged} message="Your changes will not be saved if you continue." />
                <ReactModal
                    isOpen={this.state.isModalOpen} onRequestClose={this.closeDialog}
                    overlayClassName="modal-overlay" className="modal-content" portalClassName="modal-portal"
                    bodyOpenClassName="modal-open"
                    contentLabel="Confirm Delete">
                    <form className="input-form" onSubmit={this.doRealDelete}>
                        <section className="info-block">
                            <header>
                                <h3>Confirm Delete</h3>
                            </header>

                            <span>Are you sure you want to delete this device?</span>
                            <p>{this.state.name}</p>
                            <div className="action-panel-dialog">
                                <button type="submit" className="button red push-right">Yes, Delete</button>
                                <button type="button" className="button" onClick={this.closeDialog}>No</button>
                            </div>
                        </section>
                    </form>
                </ReactModal>
                <form className="input-form" onSubmit={this.submitForm}>
                    <div className="column-root">
                        <section className="info-block">
                            <header>
                                <h3>Basic Information</h3>
                            </header>

                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-name" className="required">Name</label>
                                    <ValidateInput type="text" ref={this.receiveValidationRef} id="frm-name" name="name" value={this.state.name} onChange={this.formInputChanged} autoComplete="off_please_chrome" isRequired={true} />
                                </div>
                            </section>

                            <section>
                                <div className="input-form-group">
                                    <label>Auxiliary Information</label>
                                    <div className="input-form-group">
                                        <div className="input-form-group-check-inline">
                                            <Checkbox fancy={true} id="frm-use-lot" name="usesLot" checked={this.state.usesLot} onChange={this.formInputChanged} autoComplete="off_please_chrome">Uses Lot Number</Checkbox>
                                        </div>
                                    </div>
                                    <div className="input-form-group">
                                        <div className="input-form-group-check-inline">
                                            <Checkbox fancy={true} id="frm-use-serial" name="usesSerial" checked={this.state.usesSerial} onChange={this.formInputChanged} autoComplete="off_please_chrome">Uses Serial Number</Checkbox>
                                        </div>
                                    </div>
                                    <div className="input-form-group">
                                        <div className="input-form-group-check-inline">
                                            <Checkbox fancy={true} id="frm-use-expiration" name="usesExpiration" checked={this.state.usesExpiration} onChange={this.formInputChanged} autoComplete="off_please_chrome">Uses Expiration Date</Checkbox>
                                        </div>
                                    </div>
                                    <div className="input-form-group">
                                        <div className="input-form-group-check-inline">
                                            <Checkbox fancy={true} id="frm-use-size" name="usesSize" checked={this.state.usesSize} onChange={this.formInputChanged} autoComplete="off_please_chrome">Uses Size</Checkbox>
                                        </div>
                                    </div>
                                </div>
                            </section>
                        </section>
                        <section className="block-spacer" />
                    </div>
                    &nbsp;
                    {
                        this.state.submitStatus === true
                            ? <div className="spinner lds-dual-ring" />
                            : typeof this.state.submitStatus === "string"
                                ? <span className="error-text">{this.state.submitStatus}</span>
                                : ""
                    }
                    <div className="action-panel">
                        <button type="submit" className="button push-right" onClick={this.submitForm} disabled={this.state.submitStatus === true}>Submit</button>
                        <button type="button" className="button" onClick={this.cancelForm} disabled={this.state.submitStatus === true}>Cancel</button>
                    </div>
                </form>

            </div>
        );
    }

    private receiveValidationRef(input: ValidateInput) {
        this.inputs.push(input);
    }

    private async submitForm(e: FormEvent) {
        e.preventDefault();

        let isAllValid = true;
        for (const input of this.inputs) {
            isAllValid = input.validate() && isAllValid;
        }

        if (!isAllValid) {
            return;
        }

        const data = {
            name: this.state.name,

            usesExpiration: this.state.usesExpiration,
            usesLot: this.state.usesLot,
            usesSerial: this.state.usesSerial,
            usesSize: this.state.usesSize,
        };

        try {
            this.setState({
                submitStatus: true
            });

            if (this.state.isEdit) {
                await V2.patch(`glossary/devices/${this.props.match.params.id}`, data);
            }
            else {
                await V2.post("glossary/devices", data);
            }

            this.setState({
                hasChanged: false,
                submitStatus: false
            });

            this.props.history.goBack();
        }
        catch (e) {
            this.setState({
                submitStatus: e.message
            });
        }
    }

    private cancelForm() {
        this.props.history.goBack();
    }

    private formInputChanged(e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement> | ChangeEvent<HTMLSelectElement>, callback?: (state: Partial<ISurgeryDeviceCreationViewState>) => void) {
        const name = e.currentTarget.name;
        const value = e.currentTarget.type === "checkbox" ? (e.currentTarget as HTMLInputElement).checked : e.currentTarget.value;

        this.setState(s => {
            if (!s.changedFields.some(a => a === name)) {
                s.changedFields.push(name);
            }

            const newState = {
                changedFields: s.changedFields,
                hasChanged: true,
                [name]: value
            }

            if (callback) {
                callback(newState);
            }

            return newState;
        });
    }

    private handleDelete() {
        this.setState({
            isModalOpen: true
        });
    }

    private async doRealDelete() {
        try {
            this.setState({
                isModalOpen: false,
                submitStatus: true,
            });

            await V2.delete(`glossary/devices/${this.props.match.params.id}`);

            this.setState({
                hasChanged: false,
                submitStatus: false
            });

            this.props.history.goBack();
        }
        catch (e) {
            this.setState({
                submitStatus: e.message
            });
        }
    }

    private closeDialog() {
        this.setState({
            isModalOpen: false
        });
    }
}

export default withTitle("Add/Edit Surgery Device", SurgeryDeviceCreationView);
